// src/pages/Products.tsx
import React, { useState, useMemo } from 'react';
import { Button, Modal, Select, message, Space } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import ProductHeader from '../components/ProductHeader';
import MainpageSearch from '../components/mainpageSearch';
import { ImportProductsButton } from '../components/common/ImportProductsButton';
import { apiCreateProduct, apiUpdateProduct, isMysqlBacked } from '../services/productAPI';

import IgnitionCoilForm from '../components/forms/IgnitionCoilForm';
import SparkPlugForm from '../components/forms/SparkPlugForm';
import WiresForm from '../components/forms/WiresForm';
import SensorForm from '../components/forms/SensorForm';
import SteeringClockForm from '../components/forms/SteeringClockForm';

import {
  IgnitionCoilItem,
  SparkPlugItem,
  WiresItem,
  SensorItem,
  SteeringClockItem
} from '../types/ProductItems';

import { STORE_NAMES, StoreName } from '../utils/db';

const Products: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [selectedType, setSelectedType] = useState<string>('');

  const location = useLocation();

  const productTypes = [
    'Катушки Зажигания',
    'Свечи Зажигания',
    'Наконечники и Проводки',
    'Датчики',
    'Рулевые Шлейфы'
  ];

  // ---- helper: persist a new product to MySQL (create) or update if it already has a numeric id
  const persist = async (store: StoreName, item: any) => {
    if (isMysqlBacked(item)) {
      await apiUpdateProduct(item, store);
    } else {
      const newId = await apiCreateProduct(item, store);
      item.id = newId;
    }
  };

  const saveCoil = async (item: IgnitionCoilItem) => {
    await persist(STORE_NAMES.coils, item);
    message.success('Катушка сохранена');
    setIsModalOpen(false);
  };

  const savePlug = async (item: SparkPlugItem) => {
    await persist(STORE_NAMES.sparkPlugs, item);
    message.success('Свеча сохранена');
    setIsModalOpen(false);
  };

  const saveWire = async (item: WiresItem) => {
    await persist(STORE_NAMES.wires, item);
    message.success('Наконечник/провод сохранён');
    setIsModalOpen(false);
  };

  const saveSensor = async (item: SensorItem) => {
    await persist(STORE_NAMES.sensors, item);
    message.success('Датчик сохранён');
    setIsModalOpen(false);
  };

  const saveClock = async (item: SteeringClockItem) => {
    await persist(STORE_NAMES.steeringClocks, item);
    message.success('Рулевой шлейф сохранён');
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setCurrentStep(1);
    setSelectedType('');
  };

  const handleNext = () => {
    if (selectedType) setCurrentStep(2);
  };

  const { title, addButton } = useMemo(() => {
    if (location.pathname === '/products') {
      return {
        title: 'Список товаров',
        addButton: (
          <Space>
            <ImportProductsButton />
            <Button type="primary" onClick={handleOpenModal}>
              Добавить товар
            </Button>
          </Space>
        ),
      };
    }
    if (location.pathname.includes('/coils'))   return { title: 'Катушки зажигания' };
    if (location.pathname.includes('/plugs'))   return { title: 'Свечи зажигания' };
    if (location.pathname.includes('/wires'))   return { title: 'Наконечники и проводки' };
    if (location.pathname.includes('/sensors')) return { title: 'Датчики' };
    if (location.pathname.includes('/clocks'))  return { title: 'Рулевые шлейфы' };
    return { title: 'Товары' };
  }, [location.pathname]);

  return (
    // overflowX скрыт на уровне страницы: таблица товаров шире телефона и
    // прокручивается внутри себя (scroll={{ x: 1200 }}), но без этого её
    // ширина растягивала всю страницу — экран ездил вбок вместе с шапкой.
    <div style={{ padding: '20px', overflowX: 'hidden' }}>
      <ProductHeader title={title} addButton={addButton} />

      <div style={{ marginTop: '20px' }}>
        {location.pathname === '/products' && <MainpageSearch />}
        <Outlet />
      </div>

      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        title="Добавление нового товара"
        width={650}
        destroyOnHidden 
        maskClosable={false}
      >
        {currentStep === 1 ? (
          <>
            <p>Выберите тип товара:</p>

            <Select
              style={{ width: '100%' }}
              placeholder="Выберите тип"
              value={selectedType || undefined}
              onChange={(value) => setSelectedType(value ?? '')}
              options={productTypes.map((type) => ({ label: type, value: type }))}
            />

            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Button
                type="primary"
                disabled={!selectedType}
                onClick={handleNext}
              >
                Далее
              </Button>
            </div>
          </>
        ) : (
          <>
            <p>
              Форма для: <b>{selectedType}</b>
            </p>

            {selectedType === 'Катушки Зажигания' && (
              <IgnitionCoilForm onAdd={saveCoil} />
            )}

            {selectedType === 'Свечи Зажигания' && (
              <SparkPlugForm onAdd={savePlug} />
            )}

            {selectedType === 'Наконечники и Проводки' && (
              <WiresForm onAdd={saveWire} />
            )}

            {selectedType === 'Датчики' && (
              <SensorForm onAdd={saveSensor} />
            )}

            {selectedType === 'Рулевые Шлейфы' && (
              <SteeringClockForm onAdd={saveClock} />
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default Products;
